import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  TextField,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Typography,
  InputAdornment,
  Box,
  IconButton,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import paymentportalservice from '../../services/paymentportalservice';

// React.memo: only the 2 rows whose isSelected changes re-render per click — no virtual list needed
const CampaignRow = React.memo(({ campaign, isSelected, onSelect }) => {
  const subtitle = campaign.platform?.[0]?.name || campaign.category?.[0]?.name || null;
  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        onClick={() => onSelect(campaign)}
        sx={{
          borderRadius: '10px',
          px: 2,
          py: 1.2,
          transition: 'background 0.1s, border-color 0.1s',
          background: isSelected ? 'rgba(255,82,0,0.06)' : 'transparent',
          border: `1px solid ${isSelected ? 'rgba(255,82,0,0.2)' : 'transparent'}`,
          '&:hover': {
            background: isSelected ? 'rgba(255,82,0,0.09)' : '#F9FAFB',
            border: `1px solid ${isSelected ? 'rgba(255,82,0,0.28)' : '#E5E7EB'}`,
          },
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: isSelected
              ? 'linear-gradient(135deg, #FF5200, #FF8C00)'
              : '#D1D5DB',
            flexShrink: 0,
            mr: 1.5,
            transition: 'background 0.1s',
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: isSelected ? '#111827' : '#374151',
              fontWeight: isSelected ? 600 : 400,
              fontSize: 13.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {campaign.name}
          </Typography>
          {subtitle && (
            <Typography sx={{ color: '#9CA3AF', fontSize: 11.5, mt: 0.1 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {isSelected && (
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF5200, #FF8C00)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ml: 1,
              boxShadow: '0 2px 8px rgba(255,82,0,0.35)',
            }}
          >
            <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />
          </Box>
        )}
      </ListItemButton>
    </ListItem>
  );
});
CampaignRow.displayName = 'CampaignRow';

// ── Main component ───────────────────────────────────────────────────────────
const CampaignPickerModal = ({ open, onClose, onSelect }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  // String ID avoids object identity issues; ref holds the full object for Attach
  const [selectedId, setSelectedId] = useState(null);
  const selectedCampaignRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    paymentportalservice
      .FetchCampaigns()
      .then((data) => {
        if (data == null) { setError('Failed to load campaigns. Please try again.'); return; }
        const list = Array.isArray(data) ? data : data?.data ?? [];
        setCampaigns(list);
      })
      .catch(() => setError('Failed to load campaigns. Please try again.'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleRowSelect = useCallback((campaign) => {
    // Clicking the already-selected row deselects it, instead of being a no-op.
    setSelectedId((prev) => {
      if (prev === campaign._id) {
        selectedCampaignRef.current = null;
        return null;
      }
      selectedCampaignRef.current = campaign;
      return campaign._id;
    });
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return campaigns;
    const q = search.toLowerCase();
    return campaigns.filter((c) => c.name?.toLowerCase().includes(q));
  }, [campaigns, search]);

  const handleClose = useCallback(() => {
    setSelectedId(null);
    selectedCampaignRef.current = null;
    setSearch('');
    onClose();
  }, [onClose]);

  const handleAttach = useCallback(() => {
    if (!selectedCampaignRef.current) return;
    onSelect(selectedCampaignRef.current);
    handleClose();
  }, [onSelect, handleClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          border: '1px solid #E5E7EB',
          boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
        },
      }}
    >
      {/* Orange top stripe */}
      <Box sx={{ height: 4, background: 'linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)', flexShrink: 0 }} />

      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #F3F4F6',
          background: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38, height: 38, borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(255,82,0,0.1), rgba(255,140,0,0.06))',
              border: '1px solid rgba(255,82,0,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <CampaignOutlinedIcon sx={{ color: '#FF5200', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
              Select Campaign
            </Typography>
            <Typography sx={{ color: '#9CA3AF', fontSize: 12, mt: 0.25 }}>
              {loading ? 'Loading campaigns…' : `${campaigns.length} campaigns available`}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: '#9CA3AF',
            background: '#F9FAFB',
            border: '1px solid #E5E7EB',
            '&:hover': { background: '#F3F4F6', color: '#374151' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ px: 3, pt: 2, pb: 1.5, background: '#fff' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search campaigns…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              background: '#F9FAFB',
              borderRadius: '10px',
              color: '#111827',
              fontSize: 13.5,
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#D1D5DB' },
              '&.Mui-focused fieldset': { borderColor: '#FF5200', borderWidth: '1.5px' },
            },
            '& input::placeholder': { color: '#9CA3AF', opacity: 1 },
          }}
        />
      </Box>

      {/* List */}
      <Box
        sx={{
          minHeight: 200,
          maxHeight: 340,
          overflowY: 'auto',
          px: 1.5,
          pb: 1,
          background: '#fff',
          willChange: 'scroll-position',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: '#F9FAFB' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,82,0,0.25)', borderRadius: 4 },
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: '#FF5200' }} />
          </Box>
        )}
        {!loading && error && (
          <Typography sx={{ color: '#EF4444', textAlign: 'center', py: 5, fontSize: 13 }}>
            {error}
          </Typography>
        )}
        {!loading && !error && filtered.length === 0 && (
          <Typography sx={{ color: '#9CA3AF', textAlign: 'center', py: 5, fontSize: 13 }}>
            No campaigns found
          </Typography>
        )}
        {!loading && !error && filtered.length > 0 && (
          <List disablePadding>
            {filtered.map((campaign) => (
              <CampaignRow
                key={campaign._id}
                campaign={campaign}
                isSelected={selectedId === campaign._id}
                onSelect={handleRowSelect}
              />
            ))}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3, py: 2,
          borderTop: '1px solid #F3F4F6',
          display: 'flex',
          gap: 1.5,
          background: '#FAFAFA',
        }}
      >
        <Button
          fullWidth
          onClick={handleClose}
          sx={{
            color: '#6B7280', background: '#fff', borderRadius: '10px',
            py: 1.1, fontSize: 13.5, fontWeight: 500, textTransform: 'none',
            border: '1px solid #E5E7EB', boxShadow: 'none',
            '&:hover': { background: '#F9FAFB', borderColor: '#D1D5DB', color: '#374151' },
          }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          disabled={!selectedId}
          onClick={handleAttach}
          sx={{
            background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
            borderRadius: '10px', py: 1.1, fontSize: 13.5, fontWeight: 600,
            textTransform: 'none', boxShadow: '0 4px 14px rgba(255,82,0,0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
              opacity: 0.9, boxShadow: '0 6px 20px rgba(255,82,0,0.45)',
            },
            '&.Mui-disabled': { background: '#F3F4F6', color: '#9CA3AF', boxShadow: 'none' },
          }}
        >
          Attach Campaign
        </Button>
      </Box>
    </Dialog>
  );
};

export default CampaignPickerModal;
